import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useEffect, useReducer } from "react";

import {
  addCategory,
  addSubcategory,
  getCategories,
  clearMessage,
} from "@/services/categoryServices/categoryAction";
import { getAllProperties } from "@/services/productServices/productActions";
import NotificationAlertOptions from "@/layouts/Alerts";
import StandardizationString from "@/utils/standardization_string";

import AddCategorieView from "./AddCategorieView";
import { addCategoryForm, CODE_PATTERN, type ParentValue } from "./addCategoryForm";
import { propertyRows, type PropertyRecord } from "./propertyRows";

// SCRUM-1705's helper moved to the form slice (SCRUM-2217); re-exported for its importers.
export { codeFromName } from "./addCategoryForm";

const formActions = addCategoryForm.actions;

/** A react-select option for the parent; "None" carries `_id: 0`. */
type ParentOption = { value: ParentValue; label: string };

const AddCategorieContainer = () => {
  const dispatch = useAppDispatch();

  // Redux slices
  const categoriesSlice = useAppSelector((s) => (s.categories || {}) as any);
  const productsSlice   = useAppSelector((s) => (s.products  || {}) as any);
  const errorsSlice     = useAppSelector((s) => s.errors    || {});
  const permission      = useCurrentPermission();
  const currentWebsite  = useAppSelector((s) => s.sites?.site || {});

  // SCRUM-2217: the form state is one createSlice (addCategoryForm.ts), mounted
  // locally; the handlers below only dispatch its named actions.
  const [form, dispatchForm] = useReducer(addCategoryForm.reducer, addCategoryForm.getInitialState());
  const { parent, name, slug, code, hostsProducts, selectedPropertyIds, validation } = form;

  // Fetch categories & properties (componentDidMount)
  useEffect(() => {
    dispatch(getCategories());
    dispatch(getAllProperties());
  }, [dispatch]);

  // Handle global errors
  useEffect(() => {
    if (errorsSlice?.errors?.length) {
      dispatchForm(formActions.serverErrorsReceived(errorsSlice.errors));
    } else if (errorsSlice?.error === "DUPLICATE_CODE") {
      // SCRUM-1705: the 409 body of both create routes (SCRUM-1673) names the
      // node that already carries the code; it used to fall through unseen.
      const existing = errorsSlice.existingId ? ` (node ${errorsSlice.existingId})` : "";
      dispatchForm(formActions.duplicateCode(existing));
    }
  }, [errorsSlice, dispatch]);

  // Handle success message
  useEffect(() => {
    const msg = categoriesSlice?.message;
    if (msg) {
      dispatchForm(formActions.saved());
      NotificationAlertOptions("success", msg);
      dispatch(clearMessage());
    }
  }, [categoriesSlice?.message, dispatch]);

  // Parent options from the store, plus "None", derived during render.
  const selectParentOptions: ParentOption[] = [
    ...(categoriesSlice.categories || []).map((c) => ({
      value: { _id: c._id, name: c.name, slug: c.slug },
      label: c.name,
    })),
    { value: { _id: 0, name: "None", slug: "" }, label: "None" },
  ];

  // The property section shows once a real parent is chosen.
  const showProps = Boolean(parent) && (parent as ParentValue).name !== "None";

  const rows = propertyRows(productsSlice.properties as PropertyRecord[] | undefined, selectedPropertyIds);
  // The selected ids in table order (what the POST carries); an id no longer in
  // the store's list drops out.
  const orderedSelectedIds = rows.filter((r) => r.added).map((r) => r.key);

  // Per-site label for display
  const reformattedProperties = rows.map((prop) => {
    if (typeof prop.label !== "string" && Array.isArray(prop.label) && prop.label.length > 0) {
      return {
        ...prop,
        label: prop.label.filter((l) => l.siteid === currentWebsite?._id),
      };
    } else if (typeof prop.label !== "string" && Array.isArray(prop.label) && prop.label.length === 0) {
      return { ...prop, label: [{ value: { label: "" } }] };
    } else {
      return { ...prop, label: [{ value: { label: prop.label } }] };
    }
  });

  // Badges data (names) for selected properties
  const selectedBadges = rows.filter((r) => r.added).map((r) => r.name || r.key);

  const handleSelectParent = (opt) => dispatchForm(formActions.parentSelected(opt ? opt.value : ""));
  const handleChangeName = (e) => dispatchForm(formActions.nameChanged(e.target.value));
  const handleChangeCode = (e) => dispatchForm(formActions.codeChanged(e.target.value));
  const handleChangeSlug = (e) => dispatchForm(formActions.slugChanged(e.target.value));
  const handleChangeHostsProducts = (e) => dispatchForm(formActions.hostsProductsChanged(Boolean(e.target.checked)));
  const handleToggleProperty = (row) => dispatchForm(formActions.propertyToggled(row.key));

  // Submit (add category or subcategory)
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!parent) {
      NotificationAlertOptions("error", "Required Inputs");
      dispatchForm(formActions.parentMissing());
      return;
    }
    // SCRUM-1705: the route refuses a missing or malformed code with a 400;
    // say so before the round trip, in the field.
    if (!CODE_PATTERN.test(code) || code.length > 64) {
      NotificationAlertOptions("error", "Required Inputs");
      dispatchForm(formActions.codeRejected());
      return;
    }

    // If None → add top-level category
    if (parent.name === "None") {
      const payload = {
        name: name.trim(),
        slug: StandardizationString(name),
        code,
      };
      dispatch(addCategory(payload));
      return;
    }

    // Else ==> add subcategory attached to a parent category
    const subPayload = {
      name: name.trim(),
      slug: StandardizationString(name),
      code,
      properties: [{ key: currentWebsite?.country_code, value: orderedSelectedIds }],
      category: parent, // {_id, name, slug}
      hostsProducts,
    };
    dispatch(addSubcategory(subPayload));
  };

  // Permissions check
  const canWrite = Boolean(permission?.publish || permission?.edit);

  return (
    <AddCategorieView
      validation={validation}
      name={name}
      slug={slug}
      code={code}
      parentCategorie={parent}
      selectParentOptions={selectParentOptions}
      showProps={showProps}
      hostsProducts={hostsProducts}
      propertiesTable={reformattedProperties}
      selectedBadges={selectedBadges}
      countryCode={currentWebsite?.country_code}
      onSelectParent={handleSelectParent}
      onChangeName={handleChangeName}
      onChangeSlug={handleChangeSlug}
      onChangeCode={handleChangeCode}
      onChangeHostsProducts={handleChangeHostsProducts}
      onToggleProperty={handleToggleProperty}
      onSubmit={handleSubmit}
      canWrite={canWrite}
    />
  );
};

export default AddCategorieContainer;
